/**
 * Camera Smoke Detections layer (Phase E.W.5) — PRE-FUSION camera-CV smoke
 * verdicts from the E.W.2 `camera_smoke_detections` table (the Sonnet confirm-
 * stage positives), the camera-CV sibling of the satellite Raw Hot Spots
 * (E.W.0.5).
 *
 * Per operator-cop-doctrine §3B.8 camera smoke lives in the AMBIENT register: a
 * SMALL amber dot carrying a CAMERA glyph (the family's visual signature), NEVER
 * pulsing, distinct from the vivid Response-Level smoke_detected Anomaly-Queue
 * dots (which stay the act-register surface — untouched here) and from the white
 * live-camera glyph. The glyph reads "a camera saw smoke"; the dot beneath is the
 * always-rendered anchor + click target (the glyph rasterizer can return null in
 * some contexts, so the dot guarantees the marker is present and pickable).
 *
 * Render gate (build.md § L.2, the Raw Hot Spots precedent): HIDDEN at country
 * zoom, visible regional+. Each dot + glyph carries a distanceDisplayCondition
 * with far = REGIONAL_VISIBLE_FAR_M so the national Default view never clutters.
 *
 * Imperative installer (MapView owns the viewer), mirroring RawHotspotsLayer:
 * requestRefresh on camera moves, setVisible on the toggle, pick from the central
 * click handler → CameraSmokeFeature → the CameraSmokeBody side panel.
 */
import * as Cesium from 'cesium';
import type { CameraSmokeFeature, CameraSmokeFeatureCollection } from '@tsr/shared-types';
import { fetchCameraSmoke, type BboxQuery, type CameraSmokeFetchOpts } from '../api';
import {
  CAMERA_SMOKE_COLOR,
  CAMERA_SMOKE_DOT_SIZE,
  CAMERA_SMOKE_DOT_ALPHA,
  CAMERA_SMOKE_GLYPH_SIZE,
} from '../styles/cameraSmoke';
import { renderLucideToDataUrl } from '../iconRegistry';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

const REFRESH_DEBOUNCE_MS = 300;
const SMOKE_PREFIX = 'camera-smoke:';
const SMOKE_GLYPH_PREFIX = 'camera-smoke-glyph:';

export interface CameraSmokeLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: CameraSmokeFetchOpts,
  ) => Promise<CameraSmokeFeatureCollection>;
}

export interface CameraSmokeLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its detection feature (null if not ours). */
  pick(entity: unknown): CameraSmokeFeature | null;
  destroy(): void;
}

export function installCameraSmokeLayer(
  viewer: Cesium.Viewer,
  opts: CameraSmokeLayerOptions = {},
): CameraSmokeLayerHandle {
  const fetcher = opts.fetcher ?? fetchCameraSmoke;
  const ds = new Cesium.CustomDataSource('camera-smoke');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the L.2 render gate). Shared by every
  // dot + glyph so the whole field tiers in together as the eye descends.
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  // The amber camera glyph (tinted Lucide 'cctv'); cached by the registry. Null in
  // canvas-less contexts — the dot beneath still renders, so the marker is never
  // missing.
  const glyphUrl = renderLucideToDataUrl('cctv', {
    color: CAMERA_SMOKE_COLOR,
    size: CAMERA_SMOKE_GLYPH_SIZE,
  });

  const entityToFeature = new Map<Cesium.Entity, CameraSmokeFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load resolving after a newer one.
  let loadGen = 0;

  const render = (fc: CameraSmokeFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      const [lon, lat] = feature.geometry.coordinates;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);

      // Ambient anchor dot — a small amber dot, NEVER pulses (§3B.8 motion budget
      // reserves motion for the scored Anomaly-Queue dots). Always rendered, the
      // click target beneath the glyph.
      const dot = ds.entities.add({
        id: `${SMOKE_PREFIX}${feature.id}`,
        position,
        point: {
          pixelSize: CAMERA_SMOKE_DOT_SIZE,
          color:
            Cesium.Color.fromCssColorString(CAMERA_SMOKE_COLOR).withAlpha(CAMERA_SMOKE_DOT_ALPHA),
          outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
          outlineWidth: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(dot, feature);

      // Camera glyph overlay — the family's visual signature ("a camera saw
      // smoke"). Maps back to the same feature so a pick on the glyph opens the
      // panel too.
      if (glyphUrl) {
        const glyph = ds.entities.add({
          id: `${SMOKE_GLYPH_PREFIX}${feature.id}`,
          position,
          billboard: {
            image: glyphUrl,
            width: CAMERA_SMOKE_GLYPH_SIZE,
            height: CAMERA_SMOKE_GLYPH_SIZE,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            distanceDisplayCondition: gate,
          },
        });
        entityToFeature.set(glyph, feature);
      }
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchCameraSmoke failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): CameraSmokeFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
